import { Box, Dialog, IconButton, Divider } from '@mui/material';
import { Close } from '@styled-icons/evaicons-solid/Close';
import { DialogTitle, ThemeButton } from 'DesignSystem';
import React, { FC } from 'react';
import { IDefaultDialogProps } from 'types/dialog.types';
import { IPartnerRequest } from 'FeatureModules/Partner/types/partner.types';
import ReceivedRequestCard from 'FeatureModules/Home/Components/Requests/ReceivedRequestCard';
import { ThemeTypography, CardContainer } from 'DesignSystem';
import CompanyNameLogo from 'Components/CompanyNameLogo';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import DocumentIntroTitle from 'FeatureModules/Document/Components/DocumentIntroTitle';
import { DocumentViewer } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import useDialog from 'hooks/useDialog';
import { useLogEvent } from 'hooks/useLogEvent';
import usePartnerRequestActions from 'FeatureModules/Partner/Components/PartnerRequests/usePartnerRequestActions';
import useMessages from 'hooks/useMessages';
import { styled } from '@mui/material/styles';

interface IAcceptRequestDialogProps extends IDefaultDialogProps {
  request: IPartnerRequest;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  margin: 'auto',

  '& .MuiDialog-paper': {
    width: '90%',
    maxWidth: '100%',
    maxHeight: 650,
  },
}));

const CloseButton = styled(IconButton)(() => ({
  position: 'absolute',
  top: 0,
  right: 0,
  zIndex: 1,
}));

const DialogContent = styled('div')(() => ({
  display: 'flex',
  width: '100%',
  overflow: 'hidden',
  position: 'relative',
}));

const Column = styled('div')(({ theme }) => ({
  padding: theme.spacing(2, 4),
}));

const RequestDetailsColumn = styled(Column)(({ theme }) => ({
  width: '50%',
  overflow: 'auto',
  boxShadow: theme.custom.shadows[1],
  zIndex: 2,
}));

const DocumentPreviewColumn = styled(Column)(({ theme }) => ({
  width: '50%',
  maxHeight: '100%',
  overflow: 'auto',
  padding: 0,
  backgroundColor: theme.custom.themeColors.grayScale[5],
  marginBottom: theme.spacing(10),
}));

const ActionBar = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  width: '50%',
  right: 0,
  backgroundColor: '#fff',
  height: theme.spacing(10),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(0, 3),
  marginTop: theme.spacing(2),
  boxShadow: theme.custom.shadows[1],
}));

const AcceptRequestDialog: FC<IAcceptRequestDialogProps> = ({ onClose, request }) => {
  const { openDialog } = useDialog();
  const { logEvent } = useLogEvent();
  const { updatePartnerRequest } = usePartnerRequestActions();
  const { setErrorMessage } = useMessages();

  const handleAccept = () => {
    openDialog({
      type: 'ALERT',
      props: {
        title: 'Accept Document',
        submitText: 'Accept and close request',
        text: `Are you sure you want to accept this document? The request will be closed and considered completed.`,
        onSubmit: handleAcceptConfirm,
        onCancel: () => undefined,
      },
    });
  };

  const handleAcceptConfirm = () => {
    updatePartnerRequest({
      variables: {
        id: request.id,
        input: {
          requestStatus: 'PROVIDED_COMPLETED',
        },
      },
      onError: () =>
        setErrorMessage('Something went wrong finishing the request. Please try again later.'),
      onCompleted: () => {
        logEvent('PARTNER_REQUEST_PROVIDED_COMPLETED', {
          requestType: request.requestType,
        });
        onClose?.();
      },
    });
  };

  const handleEditRequest = () => {
    openDialog({
      type: 'PARTNER_REQUEST',
      props: {
        onClose,
        initialRequest: request,
        requestedToCompany: request.requestedToCompany,
      },
    });
  };

  return (
    <StyledDialog open onClose={onClose} data-testid="accept-request-dialog">
      <CloseButton onClick={onClose}>
        <Close size={23} />
      </CloseButton>
      <DialogContent>
        <RequestDetailsColumn>
          <Box mb={4}>
            <DialogTitle icon={<Icon size="large" name="document-request-manage" />}>
              Review Upload
            </DialogTitle>
          </Box>
          <ReceivedRequestCard displayYourMessage request={request} displayActions={false} />
          <Box mt={4} mb={3}>
            <Divider />
          </Box>
          <Box display="flex" alignItems="center" mb={1.5}>
            <CompanyNameLogo
              size={EAvailableSizes.MEDIUM}
              companyName={request.requestedToCompany.name}
            />
            <ThemeTypography variant="BODY_LARGE_BOLD">
              &nbsp;has uploaded a new document
            </ThemeTypography>
          </Box>
          {request.document && (
            <Box mb={2}>
              <CardContainer padding={1.5} selected>
                <DocumentIntroTitle document={request.document} showOpenInNewTab />
              </CardContainer>
            </Box>
          )}
        </RequestDetailsColumn>
        <DocumentPreviewColumn>
          <Box>
            {request.document && (
              <DocumentViewer file={request.document?.url} transparentBackground />
            )}
          </Box>
        </DocumentPreviewColumn>
        <ActionBar>
          <ThemeButton color="BLUE_ICE" type="button" onClick={onClose}>
            Cancel
          </ThemeButton>
          <Box display="flex" justifyContent="end">
            <ThemeButton
              color="BLUE_ICE"
              type="button"
              onClick={handleEditRequest}
              startIcon={<Icon name="edit" />}
              data-testid="edit-request-btn"
            >
              Edit request
            </ThemeButton>
            <Box mr={1} />
            <ThemeButton
              data-testid="accept-uploaded-document-btn"
              color="PRIMARY"
              type="submit"
              startIcon={<Icon name="document-request-fulfilled" color="WHITE" />}
              onClick={handleAccept}
            >
              Accept upload
            </ThemeButton>
          </Box>
        </ActionBar>
      </DialogContent>
    </StyledDialog>
  );
};

export default AcceptRequestDialog;
